// /src/services/fx.js
const KEY = (base, sym) => `fx:${base}->${sym}`;
const now = () => Date.now();

async function fetchRateExHost(base = 'EUR', symbol = 'RSD') {
  const url = `https://api.exchangerate.host/latest?base=${encodeURIComponent(base)}&symbols=${encodeURIComponent(symbol)}`;
  const res = await fetch(url, { cache: 'no-store', mode: 'cors' });
  if (!res.ok) throw new Error(`exchangerate.host HTTP ${res.status}`);
  const json = await res.json();
  const rate = Number(json?.rates?.[symbol]);
  if (!Number.isFinite(rate)) throw new Error('exchangerate.host parse');
  return { rate, provider: 'exchangerate.host', at: json.date || new Date().toISOString().slice(0,10) };
}

async function fetchRateFrankfurter(base = 'EUR', symbol = 'RSD') {
  const url = `https://api.frankfurter.dev/latest?from=${encodeURIComponent(base)}&to=${encodeURIComponent(symbol)}`;
  const res = await fetch(url, { cache: 'no-store', mode: 'cors' });
  if (!res.ok) throw new Error(`frankfurter HTTP ${res.status}`);
  const json = await res.json();
  const rate = Number(json?.rates?.[symbol]);
  if (!Number.isFinite(rate)) throw new Error('frankfurter parse');
  return { rate, provider: 'frankfurter', at: json.date || new Date().toISOString().slice(0,10) };
}

export async function getRate(base = 'EUR', symbol = 'RSD', opts = {}) {
  const ttl = (opts.ttlMinutes ?? 360) * 60 * 1000; // 6h
  const k = KEY(base, symbol);

  // 1) cache valid?
  try {
    const cached = localStorage.getItem(k);
    if (cached) {
      const { rate, provider, savedAt } = JSON.parse(cached);
      if (rate && (now() - savedAt) < ttl) {
        return { rate, provider, cached: true };
      }
    }
  } catch (e) {
    console.warn('FX cache read error', e);
  }

  // 2) try primary then fallback
  try {
    let out;
    try {
      out = await fetchRateExHost(base, symbol);
    } catch (e1) {
      console.error('FX primary failed:', e1);
      out = await fetchRateFrankfurter(base, symbol);
    }

    // guard rails
    if (opts.minRate && out.rate < opts.minRate) throw new Error('Rate too low');
    if (opts.maxRate && out.rate > opts.maxRate) throw new Error('Rate too high');

    try {
      localStorage.setItem(k, JSON.stringify({ rate: out.rate, provider: out.provider, savedAt: now() }));
    } catch (e) {
      console.warn('FX cache write error', e);
    }

    return { rate: out.rate, provider: out.provider, cached: false, asOf: out.at };
  } catch (e) {
    console.error('FX both providers failed, using last cache if any. Reason:', e);
    const cached = localStorage.getItem(k);
    if (cached) {
      const { rate, provider } = JSON.parse(cached);
      return { rate, provider: provider + ':stale', cached: true };
    }
    // Ako želiš tvrdi fallback, zadrži 117; u suprotnom, baci grešku:
    return { rate: 117.2, provider: 'fallback:const', cached: false };
    // ili: throw e;
  }
}
// === FX MAP (EUR base) ===
// === FX MAP (EUR base) ===
const MAP_KEY = (base) => `fxmap:${base}`;

async function safeFetchJson(url) {
  const res = await fetch(url, { cache: 'no-store', mode: 'cors' });
  const txt = await res.text();               // <- prvo text, pa JSON
  if (!res.ok) {
    // ubaci deo tela u grešku radi debug-a
    throw new Error(`HTTP ${res.status} @ ${url} :: ${txt.slice(0,120)}…`);
  }
  let json = null;
  try { json = JSON.parse(txt); }
  catch { throw new Error(`Non-JSON response @ ${url} :: ${txt.slice(0,120)}…`); }
  return json;
}

async function fetchFxMapEUR_primary() {
  const url = `https://api.exchangerate.host/latest?base=EUR`;
  const json = await safeFetchJson(url);
  // exchangerate.host vraća { base, rates, date, success? }
  if (!json || typeof json !== 'object' || !json.rates) {
    throw new Error('exchangerate.host: missing rates');
  }
  return { base: 'EUR', rates: json.rates, date: json.date, provider: 'exchangerate.host' };
}

async function fetchFxMapEUR_fallback() {
  const url = `https://api.frankfurter.app/latest?from=EUR`;
  const json = await safeFetchJson(url);
  if (!json || typeof json !== 'object' || !json.rates) {
    throw new Error('frankfurter: missing rates');
  }
  return { base: 'EUR', rates: json.rates, date: json.date, provider: 'frankfurter' };
}

export async function getFxMapEUR(ttlMinutes = 360) {
  const k = MAP_KEY('EUR');
  const ttl = ttlMinutes * 60 * 1000;
  const now = Date.now();

  // 1) cache
  try {
    const cached = JSON.parse(localStorage.getItem(k) || 'null');
    if (cached?.rates && (now - (cached.savedAt || 0)) < ttl) {
      return { base: 'EUR', rates: cached.rates, date: cached.date, provider: cached.provider || 'cache', cached: true };
    }
  } catch {}

  // 2) fetch primary -> fallback
  let out;
  try {
    out = await fetchFxMapEUR_primary();
  } catch (e1) {
    console.warn('[FX] primary failed:', e1?.message || e1);
    out = await fetchFxMapEUR_fallback();
  }

  // 3) minimalna validacija (npr. EUR->USD treba da bude razumna)
  const usd = Number(out.rates?.USD);
  if (!Number.isFinite(usd) || usd <= 0) {
    throw new Error('FXMAP sanity check failed (USD missing)');
  }

  // 4) cache
  try {
    localStorage.setItem(k, JSON.stringify({ ...out, savedAt: now }));
  } catch {}

  return { ...out, cached: false };
}


